import React from 'react';
import { Button, CircularProgress } from '@mui/material';

/**
 * Shared CRM button with design-system variants.
 *
 * variant:
 *   primary   — indigo gradient, white text  (CTA / add)
 *   secondary — white bg, slate border       (neutral action)
 *   outline   — transparent, indigo border   (secondary CTA)
 *   import    — indigo-tinted bg, indigo text (import/upload)
 *   filter    — white bg, slate border with hover indigo (filter)
 *   danger    — red-tinted bg, red text       (delete/destructive)
 *   ghost     — no border, slate text         (subtle)
 */

const VARIANT_SX = {
  primary: {
    background: 'linear-gradient(135deg, #0556a4, #03407b)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 2px 8px rgba(3,64,123,0.28)',
    '&:hover': {
      background: 'linear-gradient(135deg, #03407b, #022d68)',
      boxShadow: '0 4px 14px rgba(3,64,123,0.38)',
      transform: 'translateY(-1px)',
    },
    '&:active': { transform: 'translateY(0)' },
    '&.Mui-disabled': { background: '#aed6f1', color: '#fff', boxShadow: 'none' },
  },
  secondary: {
    background: '#fff',
    color: '#475569',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    '&:hover': { background: '#f8fafc', borderColor: '#cbd5e1', color: '#0f172a' },
    '&.Mui-disabled': { background: '#f8fafc', color: '#94a3b8' },
  },
  outline: {
    background: 'transparent',
    color: '#03407b',
    border: '1.5px solid #03407b',
    boxShadow: 'none',
    '&:hover': { background: '#ebf4fb', borderColor: '#02357a', color: '#02357a' },
    '&.Mui-disabled': { color: '#7fb3d3', borderColor: '#aed6f1' },
  },
  import: {
    background: '#ebf4fb',
    color: '#03407b',
    border: '1px solid #aed6f1',
    boxShadow: 'none',
    '&:hover': { background: '#d6eaf8', borderColor: '#7fb3d3', color: '#02357a' },
    '&.Mui-disabled': { background: '#f0f7fd', color: '#7fb3d3' },
  },
  filter: {
    background: '#fff',
    color: '#475569',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    '&:hover': { background: '#f8fafc', borderColor: '#03407b', color: '#03407b' },
    '&.Mui-disabled': { background: '#f8fafc', color: '#94a3b8' },
  },
  danger: {
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    boxShadow: 'none',
    '&:hover': { background: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' },
    '&.Mui-disabled': { background: '#fef2f2', color: '#fca5a5' },
  },
  ghost: {
    background: 'transparent',
    color: '#64748b',
    border: 'none',
    boxShadow: 'none',
    '&:hover': { background: '#f1f5f9', color: '#334155' },
    '&.Mui-disabled': { color: '#cbd5e1' },
  },
};

const AppButton = ({
  variant = 'primary',
  size = 'medium',
  loading = false,
  startIcon,
  endIcon,
  children,
  sx = {},
  disabled,
  ...props
}) => {
  const sizeSx =
    size === 'small'
      ? { fontSize: '1rem', px: 1.5, py: 0.625, height: 32 }
      : size === 'large'
      ? { fontSize: '1rem', px: 3, py: 1.125, height: 44 }
      : { fontSize: '1rem', px: 2, py: 0.75, height: 36 };

  return (
    <Button
      variant="contained"
      disabled={disabled || loading}
      startIcon={
        loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : startIcon
      }
      endIcon={loading ? undefined : endIcon}
      sx={{
        borderRadius: '10px',
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: "'Fira Sans', sans-serif",
        letterSpacing: '0.01em',
        transition: 'all 0.18s ease',
        lineHeight: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        ...sizeSx,
        ...(VARIANT_SX[variant] || VARIANT_SX.primary),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default AppButton;
